import React, { createContext, useContext, useState, useEffect } from 'react';
import { Session, User } from '@supabase/supabase-js';
import { profileService } from '@/services/profileService';
import { authService } from '@/services/authService';
import { Profile, AuthState } from '@/types/auth';

interface AuthContextValue extends AuthState {
  signIn: (email: string, password: string) => Promise<{ user: User | null; error: any } | undefined>;
  signOut: () => Promise<void>;
}

const AuthContext = createContext<AuthContextValue | undefined>(undefined);

export const AuthProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [state, setState] = useState<AuthState>({
    user: null,
    session: null,
    profile: null,
    loading: true,
    error: null,
  });

  const signIn = async (email: string, password: string) => {
    console.log('🔐 [AuthContext] Sign in attempt');
    setState(prev => ({ ...prev, loading: true, error: null }));

    try {
      const result = await authService.signIn(email, password);
      console.log('✅ [AuthContext] Sign in successful');
      return result;
    } catch (error: any) {
      console.error('❌ [AuthContext] Sign in error:', error);
      setState(prev => ({
        ...prev,
        error: error.message,
        loading: false,
      }));
      throw error;
    }
  };

  const signOut = async () => {
    console.log('🚪 [AuthContext] Starting sign out');

    try {
      setState({
        user: null,
        session: null,
        profile: null,
        loading: false,
        error: null,
      });

      profileService.clearCache();

      await authService.signOut();

      console.log('✅ [AuthContext] Sign out completed');
    } catch (error: any) {
      console.error('❌ [AuthContext] Sign out error:', error);
      throw error;
    }
  };

  useEffect(() => {
    let mounted = true;

    console.log('🔄 [AuthContext] Initializing auth');

    const handleAuthChange = async (event: string, session: Session | null) => {
      if (!mounted) return;

      console.log('🔐 [AuthContext] Auth state change:', event, !!session);

      if (session?.user) {
        setState(prev => ({
          ...prev,
          user: session.user,
          session: session,
          error: null,
        }));

        try {
          const profile = await profileService.loadUserProfile(session.user.id, session.user.email);
          if (mounted) {
            setState(prev => ({
              ...prev,
              profile: profile,
              loading: false,
            }));
          }
        } catch (error: any) {
          console.error('❌ [AuthContext] Error loading profile:', error);
          if (mounted) {
            setState(prev => ({
              ...prev,
              error: error.message,
              loading: false,
            }));
          }
        }
      } else {
        if (mounted) {
          setState({
            user: null,
            session: null,
            profile: null,
            loading: false,
            error: null,
          });
        }
      }
    };

    const { data: { subscription } } = authService.onAuthStateChange(handleAuthChange);

    return () => {
      mounted = false;
      subscription.unsubscribe();
    };
  }, []);

  return (
    <AuthContext.Provider value={{ ...state, signIn, signOut }}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

